import { useEffect, useMemo, useState } from 'react';
import { Icono } from '@xiltrion/ui/icono';
import {
  Filters, createDateFilterField, createFilterQuery, createFilterRule, flattenFilterRules, resolveFilterDate, toFilterDateValue,
  type FilterDateRuleValue, type FilterField, type FilterOperator, type FilterQuery, type FilterRule,
} from '@xiltrion/ui';
import { CHANNEL_LABEL, PRIORITY_LABEL, STATUS_LABEL } from '@/components/ui';
import { flattenUnits, useSpecialties, useUnitsTree, useUsers, useCaseTypes, useCourts } from './lookups';
import { ALL_STATUSES, toSearchParams, type InboxFilters } from './inboxConfig';

/** Valor de «Responsable» que equivale a «Solo mis casos». */
const ME = '__yo__';

// Solo los operadores que la API sabe aplicar: la bandeja no filtra por «no es» ni por vacío.
const IS: FilterOperator[] = [{ value: 'is', label: 'es', arity: 'one' }];
const ANY_OF: FilterOperator[] = [{ value: 'is_any_of', label: 'es alguno de', arity: 'many' }];
const OVERDUE: FilterOperator[] = [{ value: 'yes', label: 'vencido', arity: 'none' }];
const REVIEW: FilterOperator[] = [{ value: 'yes', label: 'requiere revisión', arity: 'none' }];
const IMPORTANT: FilterOperator[] = [{ value: 'yes', label: 'marcado en el Directorio', arity: 'none' }];
const FAVORITE: FilterOperator[] = [{ value: 'yes', label: 'mis favoritos', arity: 'none' }];

/** Fecha de radicación: rango, un día, desde o hasta. Se aplica sobre las filas cargadas. */
const DATE_OPERATORS = ['between', 'is', 'is_on_or_after', 'is_on_or_before'];

type SingleKey = 'organizationalUnitId' | 'specialtyId' | 'priority' | 'channel' | 'caseTypeCode' | 'courtId';
const SINGLE: Array<{ id: string; key: SingleKey }> = [
  { id: 'tipo', key: 'caseTypeCode' },
  { id: 'juzgado', key: 'courtId' },
  { id: 'area', key: 'organizationalUnitId' },
  { id: 'especialidad', key: 'specialtyId' },
  { id: 'prioridad', key: 'priority' },
  { id: 'canal', key: 'channel' },
];

const first = (v: unknown): string | undefined => (Array.isArray(v) ? (v[0] as string | undefined) : typeof v === 'string' ? v : undefined);
const iso = (d: Date | null | undefined) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined);
const fromIso = (s: string) => toFilterDateValue(new Date(`${s}T00:00:00`));

/** Los filtros de la URL como consulta de la barra (una regla por filtro activo). */
export function filtersToQuery(f: InboxFilters): FilterQuery {
  const rules: FilterRule[] = [];
  const rule = (id: string, operator: string, value?: unknown) => rules.push(createFilterRule({ id: `r-${id}`, path: [id], operator, value }));
  if (f.status?.length) rule('estado', 'is_any_of', f.status);
  if (f.mine) rule('responsable', 'is', [ME]);
  else if (f.assigneeId) rule('responsable', 'is', [f.assigneeId]);
  for (const s of SINGLE) { const v = f[s.key]; if (v) rule(s.id, 'is', [v]); }
  if (f.from && f.to) rule('radicacion', f.from === f.to ? 'is' : 'between', f.from === f.to ? fromIso(f.from) : [fromIso(f.from), fromIso(f.to)]);
  else if (f.from) rule('radicacion', 'is_on_or_after', fromIso(f.from));
  else if (f.to) rule('radicacion', 'is_on_or_before', fromIso(f.to));
  if (f.overdue) rule('sla', 'yes');
  if (f.requiresReview) rule('clasificacion', 'yes');
  if (f.important) rule('importante', 'yes');
  if (f.favorites) rule('favoritos', 'yes');
  return createFilterQuery(rules);
}

/** La consulta de la barra de vuelta a filtros de URL; conserva búsqueda y orden. Las reglas sin valor no filtran. */
export function queryToFilters(query: FilterQuery, base: InboxFilters): InboxFilters {
  const f: InboxFilters = { q: base.q, sort: base.sort, dir: base.dir };
  for (const r of flattenFilterRules(query)) {
    const field = r.path[0];
    if (!r.operator) continue;
    if (field === 'estado') { const v = Array.isArray(r.value) ? (r.value as string[]) : []; if (v.length) f.status = v; continue; }
    if (field === 'responsable') { const v = first(r.value); if (v === ME) f.mine = true; else if (v) f.assigneeId = v; continue; }
    if (field === 'sla') { f.overdue = true; continue; }
    if (field === 'clasificacion') { f.requiresReview = true; continue; }
    if (field === 'importante') { f.important = true; continue; }
    if (field === 'favoritos') { f.favorites = true; continue; }
    if (field === 'radicacion') {
      const v = r.value as FilterDateRuleValue | undefined;
      if (!v) continue;
      if (Array.isArray(v)) { f.from = iso(resolveFilterDate(v[0])); f.to = iso(resolveFilterDate(v[1])); }
      else {
        const d = iso(resolveFilterDate(v));
        if (r.operator === 'is') { f.from = d; f.to = d; }
        else if (r.operator === 'is_on_or_after') f.from = d;
        else if (r.operator === 'is_on_or_before') f.to = d;
      }
      continue;
    }
    const single = SINGLE.find((s) => s.id === field);
    if (single) { const v = first(r.value); if (v) (f as Record<string, unknown>)[single.key] = v; }
  }
  return f;
}

/** Mismos filtros, sin contar la vista rápida (al editar un chip la vista deja de aplicar). */
const same = (a: InboxFilters, b: InboxFilters) => {
  const k = (f: InboxFilters) => { const { view: _v, ...rest } = f; const sp = toSearchParams(rest); sp.sort(); return sp.toString(); };
  return k(a) === k(b);
};

/** Barra de filtros de la bandeja: cada filtro es un chip que se edita en su lugar. */
export function InboxFilterBar({ value, onChange }: { value: InboxFilters; onChange: (f: InboxFilters) => void }) {
  const units = useUnitsTree(); const specs = useSpecialties(); const users = useUsers(); const types = useCaseTypes(); const courts = useCourts();

  const fields = useMemo<FilterField[]>(() => [
    { id: 'estado', label: 'Estado', type: 'select', operators: ANY_OF, defaultOperator: 'is_any_of', searchable: false, pinSelected: true, icon: <Icono nombre="mdi:record-circle-outline" />, options: ALL_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] ?? s })) },
    { id: 'responsable', label: 'Responsable', type: 'select', operators: IS, placeholder: 'Buscar funcionario…', pinSelected: true, icon: <Icono nombre="mdi:account-check-outline" />, options: [{ value: ME, label: 'Yo (mis casos)' }, ...(users.data ?? []).map((u) => ({ value: u.id, label: u.displayName }))] },
    { id: 'tipo', label: 'Tipo', type: 'select', operators: IS, placeholder: 'Buscar tipo…', icon: <Icono nombre="mdi:tag-multiple-outline" />, options: (types.data ?? []).map((t) => ({ value: t.code, label: t.name })) },
    { id: 'area', label: 'Área', type: 'select', operators: IS, placeholder: 'Buscar área…', icon: <Icono nombre="mdi:office-building-outline" />, options: flattenUnits(units.data).map((u) => ({ value: u.value, label: u.label.trim().replace(/^└ /, '') })) },
    { id: 'juzgado', label: 'Juzgado', type: 'select', operators: IS, placeholder: 'Buscar juzgado…', icon: <Icono nombre="mdi:gavel" />, options: (courts.data ?? []).map((c) => ({ value: c.id, label: c.name })) },
    { id: 'especialidad', label: 'Especialidad', type: 'select', operators: IS, placeholder: 'Buscar especialidad…', icon: <Icono nombre="mdi:tag-outline" />, options: (specs.data ?? []).map((s) => ({ value: s.id, label: s.name })) },
    { id: 'prioridad', label: 'Prioridad', type: 'select', operators: IS, searchable: false, icon: <Icono nombre="mdi:flag-outline" />, options: Object.entries(PRIORITY_LABEL).map(([v, label]) => ({ value: v, label })) },
    { id: 'canal', label: 'Canal', type: 'select', operators: IS, searchable: false, icon: <Icono nombre="mdi:email-outline" />, options: Object.entries(CHANNEL_LABEL).map(([v, label]) => ({ value: v, label })) },
    createDateFilterField('radicacion', 'Fecha de radicación', { operators: DATE_OPERATORS, icon: <Icono nombre="mdi:calendar-range-outline" /> }),
    { id: 'sla', label: 'SLA', operators: OVERDUE, defaultOperator: 'yes', icon: <Icono nombre="mdi:timer-alert-outline" /> },
    { id: 'clasificacion', label: 'Clasificación', operators: REVIEW, defaultOperator: 'yes', icon: <Icono nombre="mdi:robot-outline" /> },
    { id: 'importante', label: 'Importante', operators: IMPORTANT, defaultOperator: 'yes', icon: <Icono nombre="griddy-icons:label-important-filled" /> },
    { id: 'favoritos', label: 'Favoritos', operators: FAVORITE, defaultOperator: 'yes', icon: <Icono nombre="bi:star" /> },
  ], [units.data, specs.data, users.data, types.data, courts.data]);

  // La barra guarda chips a medio armar (sin valor) que aún no filtran; la URL solo tiene lo que filtra.
  const [query, setQuery] = useState<FilterQuery>(() => filtersToQuery(value));
  useEffect(() => {
    setQuery((cur) => (same(queryToFilters(cur, value), value) ? cur : filtersToQuery(value)));
  }, [value]);
  return (
    <Filters
      fields={fields}
      query={query}
      onQueryChange={(q) => {
        setQuery(q);
        const next = queryToFilters(q, value);
        if (!same(next, value)) onChange(next);
      }}
      showClear
      size="sm"
    />
  );
}
